class ShopifyBundleHandler {
  constructor() {
    this.bundleComponents = [];
    this.bundleContainer = document.querySelector('[data-bundle-components]');
    this.addBundleBtn = document.querySelector('.add-bundle-to-cart');
    this.isInitialized = false;
    this.bundleConfig = window.BUNDLE_CONFIG;
    this.loadingOverlay = null;
    
    if (this.addBundleBtn && this.bundleConfig) {
      this.init();
    }
  }

  async init() {
    try {
      await this.loadBundleComponents();
      this.renderBundleComponents();
      this.bindEvents();
      this.isInitialized = true;
      
      // Calculate and display actual total price
      const totalPrice = this.calculateTotalPrice();
      this.updatePriceDisplay(totalPrice);
      this.addBundleBtn.disabled = false;
      this.addBundleBtn.textContent = `Add Complete Bundle to Cart - ₹${(totalPrice / 100).toLocaleString('en-IN')}`;
    } catch (error) {
      console.error('Failed to initialize bundle:', error);
      this.showError('Failed to load bundle components');
    }
  }

  async loadBundleComponents() {
    if (!this.bundleConfig?.componentIds || !Array.isArray(this.bundleConfig.componentIds)) {
      throw new Error('Bundle component IDs not found');
    }

    // Fetch all products and filter by our component IDs
    const response = await fetch('/products.json');
    if (!response.ok) {
      throw new Error('Failed to fetch products');
    }
    
    const data = await response.json();
    this.bundleComponents = data.products.filter(product => 
      this.bundleConfig.componentIds.includes(product.id)
    );
    
    if (this.bundleComponents.length === 0) {
      throw new Error('No valid products found');
    }
  }

  calculateTotalPrice() {
    return this.bundleComponents.reduce((total, product) => {
      const variant = product.variants?.[0];
      return total + (variant?.price || 0);
    }, 0);
  }

  updatePriceDisplay(totalPrice) {
    const priceElement = document.getElementById('dynamic-bundle-price');
    if (priceElement) {
      priceElement.textContent = `₹${(totalPrice / 100).toLocaleString('en-IN')}`;
    }
  }

  renderBundleComponents() {
    if (!this.bundleContainer || !this.bundleComponents.length) {
      this.showError('No components to display');
      return;
    }

    const totalPrice = this.calculateTotalPrice();
    const componentsHTML = this.bundleComponents
      .map(product => this.createComponentHTML(product))
      .join('');

    this.bundleContainer.innerHTML = `
      <div class="bundle-components-list">
        <h3>This bundle includes:</h3>
        ${componentsHTML}
        <div class="bundle-pricing-summary">
          <div class="bundle-price-final">
            <strong>Total Bundle Price: ₹${(totalPrice / 100).toLocaleString('en-IN')}</strong>
          </div>
          <div class="component-count">
            ${this.bundleComponents.length} Premium Components
          </div>
        </div>
      </div>
    `;
  }

  createComponentHTML(product) {
    const variant = product.variants?.[0];
    
    if (!variant) {
      console.warn(`No variants found for product ${product.id}`);
      return '';
    }
    
    const imageUrl = product.images?.[0] || '/assets/no-image.png';
    const price = variant.price ? (variant.price / 100).toLocaleString('en-IN') : '0';
    
    return `
      <div class="bundle-component-item" data-product-id="${product.id}">
        <div class="component-image">
          <img src="${imageUrl}" alt="${product.title}" loading="lazy">
        </div>
        <div class="component-details">
          <h4>${product.title}</h4>
          <p class="component-price">₹${price}</p>
          <input type="hidden" class="component-variant-id" value="${variant.id}">
        </div>
      </div>
    `;
  }

  bindEvents() {
    this.addBundleBtn.addEventListener('click', (e) => {
      e.preventDefault();
      
      if (!this.isInitialized) {
        alert('Bundle is still loading. Please wait...');
        return;
      }
      
      this.addBundleToCart();
    });
  }

  // OPTIMIZED: Single API call for all items with FIXED bundle ID
  async addBundleToCart() {
    try {
      this.addBundleBtn.disabled = true;
      this.addBundleBtn.textContent = 'Adding to Cart...';
      this.showLoadingState();

      const bundleItems = this.getBundleItems();
      const totalPrice = this.calculateTotalPrice();
      
      // Verify all items have the same bundle ID
      if (!this.verifyBundleIntegrity(bundleItems)) {
        throw new Error('Bundle integrity check failed');
      }
      
      // Add all components to cart in a single API call
      await this.addBulkItemsToCart(bundleItems);

      this.showSuccessMessage(`Complete bundle added for ₹${(totalPrice / 100).toLocaleString('en-IN')} (${bundleItems.length} components)`);
      this.updateCartUI();
      
    } catch (error) {
      console.error('Error adding bundle to cart:', error);
      this.showErrorMessage('Failed to add bundle to cart. Please try again.');
    } finally {
      this.hideLoadingState();
      this.addBundleBtn.disabled = false;
      const totalPrice = this.calculateTotalPrice();
      this.addBundleBtn.textContent = `Add Complete Bundle to Cart - ₹${(totalPrice / 100).toLocaleString('en-IN')}`;
    }
  }

  // OPTIMIZED: Bulk cart addition method
  async addBulkItemsToCart(items) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: items  // Use 'items' array for bulk addition
      })
    });

    const result = await response.json();
    
    if (!response.ok) {
      // Handle partial failures
      if (result.errors) {
        console.error('Some items failed to add:', result.errors);
        throw new Error(`Failed to add some items: ${Object.values(result.errors).join(', ')}`);
      }
      throw new Error(`Failed to add items to cart: ${result.message || 'Unknown error'}`);
    }

    return result;
  }

  // FIXED: Generate ONE bundle ID for all items
  getBundleItems() {
    // Generate ONE unique bundle identifier for the ENTIRE bundle
    const bundleId = `bundle_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const bundleTimestamp = new Date().toISOString();
    const bundleTotal = this.calculateTotalPrice();

    return this.bundleComponents.map(product => {
      const variant = product.variants?.[0];
      if (!variant) return null;

      return {
        id: variant.id,
        quantity: 1,
        properties: {
          '_bundle': 'PC Custom Bundle',
          '_bundle_id': bundleId, // SAME ID for ALL items - this is the key fix!
          '_bundle_component': 'true',
          '_component_name': product.title,
          '_bundle_created': bundleTimestamp,
          '_bundle_total_items': this.bundleComponents.length.toString(),
          '_bundle_total_price': (bundleTotal / 100).toString(),
          '_bundle_type': 'pc_components'
        }
      };
    }).filter(item => item !== null);
  }

  // Verify all items have the same bundle ID
  verifyBundleIntegrity(items) {
    const bundleIds = [...new Set(items.map(item => item.properties._bundle_id))];
    
    if (bundleIds.length > 1) {
      console.warn('Multiple bundle IDs detected:', bundleIds);
      return false;
    }
    
    console.log(`Bundle created with ID: ${bundleIds[0]} containing ${items.length} items`);
    return true;
  }

  showLoadingState() {
    this.hideLoadingState(); // Remove existing overlay first
    
    this.loadingOverlay = document.createElement('div');
    this.loadingOverlay.className = 'bundle-loading-overlay';
    this.loadingOverlay.innerHTML = `
      <div class="loading-spinner">
        <div class="spinner"></div>
        <p>Adding ${this.bundleComponents.length} items to cart...</p>
      </div>
    `;
    
    this.bundleContainer.style.position = 'relative';
    this.bundleContainer.appendChild(this.loadingOverlay);
  }

  hideLoadingState() {
    if (this.loadingOverlay && this.loadingOverlay.parentNode) {
      this.loadingOverlay.parentNode.removeChild(this.loadingOverlay);
      this.loadingOverlay = null;
    }
  }

  showSuccessMessage(message) {
    const existingMessage = document.querySelector('.bundle-success-message');
    if (existingMessage) {
      existingMessage.remove();
    }

    const successDiv = document.createElement('div');
    successDiv.className = 'bundle-success-message';
    successDiv.innerHTML = `
      <div class="alert alert-success">
        ✅ ${message}
        <a href="/cart" class="view-cart-link">View Cart</a>
      </div>
    `;
    
    this.addBundleBtn.parentNode.insertBefore(successDiv, this.addBundleBtn.nextSibling);
    
    setTimeout(() => {
      successDiv.remove();
    }, 5000);
  }

  showErrorMessage(message) {
    const existingMessage = document.querySelector('.bundle-error-message');
    if (existingMessage) {
      existingMessage.remove();
    }

    const errorDiv = document.createElement('div');
    errorDiv.className = 'bundle-error-message';
    errorDiv.innerHTML = `
      <div class="alert alert-error">
        ⚠️ ${message}
      </div>
    `;
    
    this.addBundleBtn.parentNode.insertBefore(errorDiv, this.addBundleBtn.nextSibling);
    
    setTimeout(() => {
      errorDiv.remove();
    }, 5000);
  }

  showError(message) {
    if (this.bundleContainer) {
      this.bundleContainer.innerHTML = `
        <div class="bundle-error">
          <p class="error-message">⚠️ ${message}</p>
        </div>
      `;
    }
  }

  updateCartUI() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const cartCount = document.querySelector('.cart-count, [data-cart-count]');
        if (cartCount) {
          cartCount.textContent = cart.item_count;
        }
        
        // Trigger custom cart update event
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { cart: cart }
        }));
      })
      .catch(error => console.error('Error updating cart UI:', error));
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  setTimeout(() => {
    new ShopifyBundleHandler();
  }, 100);
});
